import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '../api/auth';
import { ApiClientError } from '../api/client';
import { Icon } from '../lib/icons';
import { PreferenceToggles } from '../lib/PreferencesProvider';

/** How long before "send again" is offered. See the comment on `sentAt`. */
const RESEND_SECONDS = 60;

/**
 * FEAT-001 — asking for a password reset link.
 *
 * Built from the same primitives as `LoginPage` — `login-shell`, `login-card`,
 * `field`, `btn primary login-submit` — because it is the same moment in the
 * same journey, reached from a link on that screen.
 */
export function ForgotPasswordPage(): ReactElement {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  /*
   * The resend countdown.
   *
   * Not decoration: each request invalidates the previous link, so somebody who
   * clicks "send again" and then opens the *older* email gets a dead link and
   * reasonably concludes the product is broken. Slowing the second request down
   * makes that sequence much less likely, and the copy below says the rest.
   */
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  /** The request itself, shared by the form and the resend button. */
  async function send(): Promise<void> {
    setError(null);
    setIsSubmitting(true);

    try {
      await requestPasswordReset(email);
      setSent(true);
      setCooldown(RESEND_SECONDS);
    } catch (caught) {
      /*
       * A rejection here is a transport or rate-limit failure — never "no such
       * account", which the server deliberately does not report. So this branch
       * cannot leak whether the address exists.
       */
      setError(caught instanceof ApiClientError ? caught.message : t('login_network_error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    void send();
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <div className="login-brand">
          <img src="/logo.png" alt="Boud" />
          <div className="sub">{t('boud_sub')}</div>
        </div>

        {sent ? (
          /*
           * Deliberately identical whether or not the address exists. The server
           * returns 204 either way so that this endpoint cannot be used to
           * enumerate accounts, and a screen that said "sent" for one and "not
           * found" for the other would undo that in the interface.
           */
          <div className="login-sent" role="status" aria-live="polite">
            <h1 className="login-title">{t('reset_sent_title')}</h1>
            <p className="login-lead">{t('reset_sent_body', { email })}</p>
            <p className="login-note">{t('reset_only_newest')}</p>

            <button
              type="button"
              className="btn login-submit"
              onClick={() => void send()}
              disabled={cooldown > 0 || isSubmitting}
            >
              {cooldown > 0 ? t('resend_in', { seconds: cooldown }) : t('resend_link')}
            </button>

            <Link className="login-alt" to="/login">
              {t('back_to_sign_in')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <h1 className="login-title">{t('forgot_password_title')}</h1>
            <p className="login-lead">{t('forgot_password_intro')}</p>

            {error ? (
              <div className="login-error" role="alert">
                <Icon name="info" />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="field">
              <label htmlFor="email">{t('email')}</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <button type="submit" className="btn primary login-submit" disabled={isSubmitting}>
              {isSubmitting ? t('sending') : t('send_reset_link')}
            </button>

            <Link className="login-alt" to="/login">
              {t('back_to_sign_in')}
            </Link>
          </form>
        )}

        <div className="login-toggles">
          <PreferenceToggles />
        </div>
      </div>
    </div>
  );
}
